import { useState } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Sparkles,
  Play,
  Check,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const FirstEnvelopeStep = ({ onNext, onBack, onNavigateAway }) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState(null);

  const options = [
    {
      id: "create",
      icon: FileText,
      title: t("onboarding.firstEnvelope.create.title", "Create an Envelope"),
      description: t(
        "onboarding.firstEnvelope.create.description",
        "Upload a document and send it for signature right now.",
      ),
      destination: "/envelopes/new",
      primary: true,
    },
    {
      id: "template",
      icon: Sparkles,
      title: t(
        "onboarding.firstEnvelope.template.title",
        "Start from a Template",
      ),
      description: t(
        "onboarding.firstEnvelope.template.description",
        "Browse pre-made templates for common documents like NDAs and contracts.",
      ),
      destination: "/templates",
    },
    {
      id: "explore",
      icon: Play,
      title: t(
        "onboarding.firstEnvelope.explore.title",
        "Explore the Dashboard",
      ),
      description: t(
        "onboarding.firstEnvelope.explore.description",
        "Take a look around first. You can create your first envelope anytime.",
      ),
      destination: null, // Goes to next step (CompleteStep)
    },
  ];

  const handleContinue = () => {
    const option = options.find((o) => o.id === selectedOption);
    if (option) {
      if (option.destination) {
        // Navigate away without completing onboarding
        // User can return to /onboarding to continue
        onNavigateAway(option.destination);
      } else {
        // Go to next step (CompleteStep)
        onNext();
      }
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="max-w-3xl mx-auto px-4"
    >
      <div className="text-center mb-12">
        <motion.h2
          variants={fadeInUp}
          className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
        >
          {t(
            "onboarding.firstEnvelope.title",
            "Ready to send your first document?",
          )}
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
          {t(
            "onboarding.firstEnvelope.subtitle",
            "Choose how you'd like to get started",
          )}
        </motion.p>
      </div>

      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="space-y-4 mb-12"
      >
        {options.map((option) => (
          <motion.div
            key={option.id}
            variants={fadeInUp}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => setSelectedOption(option.id)}
            className={cn(
              "p-6 rounded-xl border-2 cursor-pointer transition-all",
              selectedOption === option.id
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 bg-card",
            )}
          >
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "w-12 h-12 rounded-lg flex items-center justify-center shrink-0",
                  option.primary
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted",
                )}
              >
                <option.icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{option.title}</h3>
                  {option.primary && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {t("common.recommended", "Recommended")}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground mt-1">
                  {option.description}
                </p>
              </div>
              <div
                className={cn(
                  "w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0",
                  selectedOption === option.id
                    ? "border-primary bg-primary"
                    : "border-muted-foreground/30",
                )}
              >
                {selectedOption === option.id && (
                  <Check className="w-4 h-4 text-primary-foreground" />
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t("common.back", "Back")}
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedOption}
          className="gap-2"
        >
          {selectedOption === "explore"
            ? t("onboarding.firstEnvelope.finish", "Finish Setup")
            : t("onboarding.firstEnvelope.letsGo", "Let's Go!")}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

export default FirstEnvelopeStep;
